import { action, computed, makeObservable, observable } from 'mobx'

import { AttachmentSortingOption } from '~/client/src/shared/enums/AttachmentsSortingOption'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import KnownTranslatorKeys from '~/client/src/shared/localization/knownTranslatorKeys'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import AttachmentsStore from '../../../../Attachments.store'

interface ISortingOption {
  id: string
  nameTranslatorKey: KnownTranslatorKeys
  caption: string
}

export default class AttachmentsSortingOptionsStore {
  @observable public isShown: boolean = false
  @observable public selectedSortingOptionId: string =
    AttachmentSortingOption.DATE_UPLOADED
  @observable public selectedSortOrder: SortOrder = SortOrder.DESC

  public constructor(
    private readonly attachmentsStore: AttachmentsStore,
    private readonly onShowChanged?: (isShown: boolean) => void,
  ) {
    makeObservable(this)
  }

  @computed
  public get sortingOptions(): ISortingOption[] {
    return [
      {
        id: AttachmentSortingOption.NAME,
        nameTranslatorKey: KnownTranslatorKeys.name,
        caption: Localization.getText(KnownTranslatorKeys.name),
      },
      {
        id: AttachmentSortingOption.FILE_CATEGORY,
        nameTranslatorKey: KnownTranslatorKeys.fileCategory,
        caption: Localization.getText(KnownTranslatorKeys.fileCategory),
      },
      {
        id: AttachmentSortingOption.FILE_TYPE,
        nameTranslatorKey: KnownTranslatorKeys.fileType,
        caption: Localization.getText(KnownTranslatorKeys.fileType),
      },
      {
        id: AttachmentSortingOption.FORM_CATEGORY,
        nameTranslatorKey: KnownTranslatorKeys.workflowCategory,
        caption: Localization.getText(KnownTranslatorKeys.workflowCategory),
      },
      {
        id: AttachmentSortingOption.FORM_TYPE,
        nameTranslatorKey: KnownTranslatorKeys.workflowType,
        caption: Localization.getText(KnownTranslatorKeys.workflowType),
      },
      {
        id: AttachmentSortingOption.SOURCE,
        nameTranslatorKey: KnownTranslatorKeys.workflowId,
        caption: Localization.getText(KnownTranslatorKeys.workflowId),
      },
      {
        id: AttachmentSortingOption.WORKFLOW_TITLE,
        nameTranslatorKey: KnownTranslatorKeys.workflowTitle,
        caption: Localization.getText(KnownTranslatorKeys.workflowTitle),
      },
      {
        id: AttachmentSortingOption.WORKFLOW_STATUS,
        nameTranslatorKey: KnownTranslatorKeys.workflowStatus,
        caption: Localization.getText(KnownTranslatorKeys.workflowStatus),
      },
      {
        id: AttachmentSortingOption.LOCATION,
        nameTranslatorKey: KnownTranslatorKeys.location,
        caption: Localization.getText(KnownTranslatorKeys.location),
      },
      {
        id: AttachmentSortingOption.COMPANY,
        nameTranslatorKey: KnownTranslatorKeys.company,
        caption: Localization.getText(KnownTranslatorKeys.company),
      },
      {
        id: AttachmentSortingOption.UPLOADED_BY,
        nameTranslatorKey: KnownTranslatorKeys.uploadedBy,
        caption: Localization.getText(KnownTranslatorKeys.uploadedBy),
      },
      {
        id: AttachmentSortingOption.DATE_UPLOADED,
        nameTranslatorKey: KnownTranslatorKeys.dateUploaded,
        caption: Localization.getText(KnownTranslatorKeys.dateUploaded),
      },
    ]
  }

  @computed
  public get currentSortingOption(): ISortingOption {
    return this.sortingOptions.find(
      option => option.id === this.selectedSortingOptionId,
    )
  }

  @computed
  public get sortByIcon(): string {
    const option = this.currentSortingOption
    if (!option) {
      return ''
    }

    return this.selectedSortOrder === SortOrder.ASC ? '↑' : '↓'
  }

  @computed
  public get sortByCaption(): string {
    const option = this.currentSortingOption
    if (!option) {
      return ''
    }

    return option.caption
  }

  @action.bound
  public toggleSortingOptions() {
    this.isShown = !this.isShown
    if (this.onShowChanged) {
      this.onShowChanged(this.isShown)
    }
  }

  @action.bound
  public hideSortingOptions() {
    this.isShown = false
    if (this.onShowChanged) {
      this.onShowChanged(this.isShown)
    }
  }

  @action.bound
  public selectSortingOption(optionId: string) {
    if (this.selectedSortingOptionId === optionId) {
      this.selectedSortOrder =
        this.selectedSortOrder === SortOrder.ASC
          ? SortOrder.DESC
          : SortOrder.ASC
    } else {
      this.selectedSortingOptionId = optionId
      this.selectedSortOrder = SortOrder.DESC
    }

    this.attachmentsStore.handleColumnSort(optionId)
    this.hideSortingOptions()
  }

  @action.bound
  public initializeFromStore() {
    const { sortState } = this.attachmentsStore
    if (sortState.columnKey) {
      this.selectedSortingOptionId = sortState.columnKey
      this.selectedSortOrder = sortState.order
    }
  }
}
